import { message } from 'antd';
import { isAxiosError } from 'axios';
import { useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type UseDebounceProps<V> = {
  beforeDebounce?: (value: V) => void;
  debounceTime?: number;
  dependencies?: React.DependencyList;
};

export default function useDebounce<T, V>(
  promiseGenerator: (value: V) => Promise<T> | void,
  onResolved: (resolution: T) => void,
  {
    beforeDebounce,
    debounceTime = 500,
    dependencies = [],
  }: UseDebounceProps<V> = {},
): { debounce: (value: V) => void; loading: boolean } {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const promiseGeneratorRef = useRef(promiseGenerator);
  const onResolvedRef = useRef(onResolved);
  const beforeDebounceRef = useRef(beforeDebounce);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Keep refs updated with latest functions
  useEffect(() => {
    promiseGeneratorRef.current = promiseGenerator;
    onResolvedRef.current = onResolved;
    beforeDebounceRef.current = beforeDebounce;
  }, [promiseGenerator, onResolved, beforeDebounce, dependencies]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, []);

  const debounce = useCallback(
    (value: V) => {
      // Cancel previous timeout
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }

      setLoading(true);
      beforeDebounceRef.current?.(value);

      const currentTimeoutId = setTimeout(async () => {
        try {
          const result = await promiseGeneratorRef.current(value);
          if (result && isMountedRef.current) {
            onResolvedRef.current(result);
          }
        } catch (error) {
          if (isMountedRef.current && isAxiosError(error)) {
            message.error(
              error.response?.data.message || t('errors.somethingWentWrong'),
            );
          }
        } finally {
          // Only update loading state if still mounted and this is still the active timeout
          if (
            isMountedRef.current &&
            timeoutIdRef.current === currentTimeoutId
          ) {
            setLoading(false);
          }
        }
      }, debounceTime);

      timeoutIdRef.current = currentTimeoutId;
    },
    [debounceTime, t],
  );

  return {
    debounce,
    loading,
  };
}
